import React, {
  ButtonHTMLAttributes,
  FormHTMLAttributes,
  useState,
} from "react";
import Modal from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import { FileText, PlusCircle } from "lucide-react";
import ImageSelector from "@/components/pages/reservations/guests/components/form/ImageSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DocumentType } from "@/types/document";
import { DocumentFormData } from "../guests/utils/reservationSchema";

interface DocumentModalProps {
  trigger: React.ReactNode;
  onSubmit: (data: DocumentFormData) => void;
  formAttributes?: FormHTMLAttributes<HTMLFormElement>;
  submitButtonAttributes?: ButtonHTMLAttributes<HTMLButtonElement>;
}

const documentTypeLabels: Record<DocumentType, string> = {
  [DocumentType.ID_CARD]: "ID Card",
  [DocumentType.PASSPORT]: "Passport",
  [DocumentType.DRIVERS_LICENSE]: "Driver's License",
  [DocumentType.BIRTH_CERTIFICATE]: "Birth Certificate",
  [DocumentType.VISA]: "Visa",
  [DocumentType.OTHER]: "Other",
};

const AddDocumentModal: React.FC<DocumentModalProps> = ({
  trigger,
  onSubmit,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [type, setType] = useState(DocumentType.PASSPORT);
  const [image, setImage] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const reset = () => {
    setFile(null);
    setType(DocumentType.PASSPORT);
    setImage("");
  };

  const handleFormSubmit = () => {
    onSubmit({ file: file || undefined, type, image });
    reset();
    setIsOpen(false);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setImage(imageUrl);
      setFile(file);
    }
  };

  return (
    <Modal
      trigger={trigger}
      title="Add New Document"
      description="Upload a new document for the guest"
      className="hide-scrollbar max-h-[70vh] overflow-y-auto"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <ImageSelector
        defaultIcon={<FileText className={"size-[72px] stroke-1"} />}
        overlayContent={
          <div
            className={
              "rounded-full bg-white absolute bottom-0 -translate-x-1/2 left-1/2"
            }
          >
            <PlusCircle className={"size-5"} />
          </div>
        }
        onImageSelect={handleImageUpload}
        selectedImage={image}
        className="mb-4"
        imageClassName={"object-contain max-h-[500px]"}
      />
      <div className="space-y-2">
        <label htmlFor="type" className="text-sm font-medium">
          Document Type <span className="text-red-500">*</span>
        </label>

        <Select
          value={type}
          onValueChange={(value) => setType(value as DocumentType)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select document type" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(documentTypeLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            reset();
          }}
        >
          Clear
        </Button>
        <Button onClick={() => handleFormSubmit()}>Add Document</Button>
      </div>
    </Modal>
  );
};

export default AddDocumentModal;
